import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { IconButton } from '@mui/material';
import { Grid, Card } from "@aws-amplify/ui-react";
import { MdSearch, MdKeyboardArrowDown } from "react-icons/md";
import AddIcon from '@mui/icons-material/Add';
import { IoBookmarkSharp } from "react-icons/io5";
import { GrDownload } from "react-icons/gr";
import { PiTrash } from "react-icons/pi";
import { getGenQADashboardApi } from '../../services/AxiosServices';
import { logError } from '../../utils/errorHandling';
import './GenQADashboard.css';

const GenQADashboard = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [showStatusDropdown, setShowStatusDropdown] = React.useState(false);
    const statusDropdownRef = useRef<HTMLDivElement | null>(null);

    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
                setShowStatusDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const toggleStatusOption = (option: string) => {
        setStatusFilter((prev) => {
            if (prev.includes(option)) return prev.filter((p) => p !== option);
            return [...prev, option];
        });
    };

    const statusActive = statusFilter.length > 0;

    const [timeRange, setTimeRange] = useState('this week');
    const [approvedRange, setApprovedRange] = useState('this week');
    const [isLoading, setIsLoading] = useState(true);
    const [counts, setCounts] = useState({
        openProjects: 0,
        openQuestions: 0,
        approvedQuestions: 0,
        timeSpent: '0h',
    });
    const [projects, setProjects] = useState<any[]>([]);

    useEffect(() => {
        let isActive = true;

        const fetchDashboard = async () => {
            try {
                const token = await getAccessTokenSilently();
                const response: any = await getGenQADashboardApi(token);
                if (!isActive) return;

                const rawProjects = Array.isArray(response?.projects)
                    ? response.projects
                    : Array.isArray(response?.result)
                        ? response.result
                        : Array.isArray(response)
                            ? response
                            : [];

                setProjects(rawProjects.map((p: any, idx: number) => ({
                    id: p.id || p.project_id || p.project_name || `Project ${idx + 1}`,
                    status: String(p.status || 'not started').toLowerCase().replace(/_/g, ' '),
                    progress: p.progress ?? p.progress_percentage ?? 0,
                    collaborators: p.collaborators ?? p.collaborators_count ?? 0,
                    created: p.created || p.created_at || '',
                    lastEdited: p.last_edited || p.lastEdited || p.updated_at || '',
                })));

                // Counts are nested under "summary"; approved_questions/time_spent carry a {count|hours, period} shape.
                const summary = response?.summary ?? response ?? {};

                const approvedRaw = summary.approved_questions ?? summary.approvedQuestions;
                const approvedQuestions = approvedRaw && typeof approvedRaw === 'object'
                    ? approvedRaw.count ?? 0
                    : approvedRaw ?? 0;
                const approvedPeriod = approvedRaw && typeof approvedRaw === 'object' ? approvedRaw.period : undefined;

                const timeSpentRaw = summary.time_spent ?? summary.timeSpent;
                const timeSpent = timeSpentRaw && typeof timeSpentRaw === 'object'
                    ? `${timeSpentRaw.hours ?? 0}h`
                    : timeSpentRaw ?? '0h';
                const timeSpentPeriod = timeSpentRaw && typeof timeSpentRaw === 'object' ? timeSpentRaw.period : undefined;

                setCounts({
                    openProjects: summary.open_projects ?? summary.openProjects ?? 0,
                    openQuestions: summary.open_questions ?? summary.openQuestions ?? 0,
                    approvedQuestions,
                    timeSpent,
                });

                if (approvedPeriod) setApprovedRange(approvedPeriod);
                if (timeSpentPeriod) setTimeRange(timeSpentPeriod);
            } catch (error) {
                logError('GenQA dashboard fetch failed', error);
            } finally {
                if (isActive) setIsLoading(false);
            }
        };

        void fetchDashboard();
        return () => {
            isActive = false;
        };
    }, [getAccessTokenSilently]);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const dropdowns = document.querySelectorAll('.stat-dropdown-menu');
            dropdowns.forEach(dropdown => {
                if (!(dropdown as HTMLElement).contains(event.target as Node)) {
                    dropdown.classList.remove('show');
                }
            });
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const stats = [
        { label: 'Open projects', value: String(counts.openProjects), variant: 'blue' },
        { label: 'Open questions', value: String(counts.openQuestions), variant: 'purple' },
        { label: 'Approved questions', value: String(counts.approvedQuestions), variant: 'pink', hasDropdown: true,
          timeRange: approvedRange, onRangeChange: setApprovedRange },
        { label: 'Time spent', value: counts.timeSpent, variant: 'volate', hasDropdown: true,
          timeRange: timeRange, onRangeChange: setTimeRange },
    ];

    // Status filter options use '_'/'-' separators; project status is normalized with spaces.
    const normalizeStatusKey = (value: string) => value.toLowerCase().replace(/[_-]/g, ' ');

    const filteredProjects = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return projects.filter((project) => {
            if (query && !String(project.id).toLowerCase().includes(query)) {
                return false;
            }
            if (statusFilter.length > 0) {
                const projectStatus = normalizeStatusKey(project.status);
                const matchesStatus = statusFilter.some((filterValue) => normalizeStatusKey(filterValue) === projectStatus);
                if (!matchesStatus) return false;
            }
            return true;
        });
    }, [projects, searchText, statusFilter]);

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h4 className="dashboard-title"> GenQ&A Dashboard</h4>
            </div>

            <div className='grid-and-add-button'>
                {/* <div  className="stats-grid"> */}
                    {stats.map((stat, index) => (
                        <Grid key={index}>
                            <Card className="stat-card">
                                <div className="stat-label">
                                    {stat.label}
                                </div>
                                <div className="stat-value-container">
                                    <div className={`stat-value ${stat.variant}`}>
                                        {stat.value}
                                    </div>
                                    {stat.hasDropdown && (
                                        <div className="stat-dropdown">
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const menu = e.currentTarget.nextElementSibling;
                                                    // Close all other dropdowns
                                                    document.querySelectorAll('.stat-dropdown-menu').forEach(el => {
                                                        if (el !== menu) el.classList.remove('show');
                                                    });
                                                    menu?.classList.toggle('show');
                                                }}
                                                className="stat-dropdown-button"
                                            >
                                                {stat.timeRange}
                                                <MdKeyboardArrowDown size={16} />
                                            </button>
                                            <div className="stat-dropdown-menu">
                                                <div onClick={() => {
                                                    stat.onRangeChange('today');
                                                    document.querySelectorAll('.stat-dropdown-menu').forEach(el => 
                                                        el.classList.remove('show')
                                                    );
                                                }}>Today</div>
                                                <div onClick={() => {
                                                    stat.onRangeChange('this week');
                                                    document.querySelectorAll('.stat-dropdown-menu').forEach(el => 
                                                        el.classList.remove('show')
                                                    );
                                                }}>This week</div>
                                                <div onClick={() => {
                                                    stat.onRangeChange('this month');
                                                    document.querySelectorAll('.stat-dropdown-menu').forEach(el => 
                                                        el.classList.remove('show')
                                                    );
                                                }}>This month</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Grid>
                    ))}

                     <IconButton className="add-button"
                onClick={() => {window.open(`/qanewproject`, "_blank")}}>
                    <AddIcon />
                </IconButton>
                {/* </div> */}
               
            </div>


            <div className="search-filter-section">
                <div className="question-dashboard-search-input">
                    <MdSearch size={20} />
                    <input
                        type="text"
                        placeholder="search"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
                <div className="dropdown-filter" ref={statusDropdownRef} tabIndex={0}>
                    <button className={`dropdown-dashboard-toggle ${statusActive ? 'filters-active' : ''}`} onClick={() => setShowStatusDropdown((s) => !s)}>
                        <span>Status</span>
                        {statusFilter.length > 0 ? <span className="count-badge">{statusFilter.length}</span> : <MdKeyboardArrowDown size={20} />}
                    </button>
                    {showStatusDropdown && (
                        <div className="dropdown-menu">
                            <label className="dropdown-item">
                                <input type="checkbox" checked={statusFilter.includes('in_progress')} onChange={() => toggleStatusOption('in_progress')} />
                                <span className="custom-checkbox" aria-hidden></span>
                                <span className="dropdown-label">In Progress</span>
                            </label>
                            <label className="dropdown-item">
                                <input type="checkbox" checked={statusFilter.includes('generating')} onChange={() => toggleStatusOption('generating')} />
                                <span className="custom-checkbox" aria-hidden></span>
                                <span className="dropdown-label">Generating</span>
                            </label>
                            <label className="dropdown-item">
                                <input type="checkbox" checked={statusFilter.includes('complete')} onChange={() => toggleStatusOption('complete')} />
                                <span className="custom-checkbox" aria-hidden></span>
                                <span className="dropdown-label">Complete</span>
                            </label>
                             <label className="dropdown-item">
                                <input type="checkbox" checked={statusFilter.includes('not-started')} onChange={() => toggleStatusOption('not-started')} />
                                <span className="custom-checkbox" aria-hidden></span>
                                <span className="dropdown-label">Not Started</span>
                            </label>
                        </div>
                    )}
                </div>

            </div>

<div className="table-dashboard-container">
                <table className="project-table">
                    <thead>
                        <tr>
                            <th>Project ID</th>
                            <th>Status</th>
                            <th>Actions</th>
                            <th>Progress</th>
                            <th>Collaborators</th>
                            <th>Created</th>
                            <th>Last edited</th>
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '24px' }}>Loading projects...</td></tr>
                        ) : filteredProjects.length === 0 ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '24px' }}>No projects found.</td></tr>
                        ) : filteredProjects.map((project, index) => (
                            <tr key={project.id || index}>
                                <td>{project.id}</td>
                                <td>
                                    <span className={`status-badge status-${project.status.replace(' ', '-')}`}>
                                        {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                                    </span>
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        <button className="action-button" title="Save">
                                            <IoBookmarkSharp />
                                        </button>
                                        <button className="action-button" title="Download">
                                            <GrDownload />
                                        </button>
                                        <button className="action-button" title="Delete">
                                            <PiTrash />
                                        </button>
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${project.progress}%` }}
                                            />
                                        </div>
                                        <span>{project.progress}%</span>
                                    </div>
                                </td>
                                <td>
                                    <div className="collaborators-count">
                                        <span>{project.collaborators}</span>
                                    </div>
                                </td>
                                <td>{project.created}</td>
                                <td>{project.lastEdited}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
           
        </div>
    );
};

export default GenQADashboard;